import {
  AdjustScoreArgs,
  clipboardScoreHistoryResponse,
  GetRestrictionResponse,
  ScoreLog,
  WorkerAttendanceProfile,
  WorkerAttendanceScoreDispute,
  WorkersAttendanceRateRequest,
} from "./types";
import request from "superagent";
import { getAuthHeader } from "../../utils/superagent";
import { DEFAULT_PAGE_NUMBER, SCORE_LOG_API_PAGE_SIZE } from "./constants";
import { environmentConfig } from "@src/appV2/environment";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";

const attendanceScoreServiceUrl = environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL;

export const getWorkerAttendanceProfile = async (
  workerUserId: string,
  withPerformanceSince?: string
): Promise<WorkerAttendanceProfile> => {
  const authHeader = await getAuthHeader();

  const { body } = await request
    .get(`${attendanceScoreServiceUrl}/workers/${workerUserId}/profile`)
    .query({
      ...(isDefined(withPerformanceSince) ? { withPerformanceSince } : {}),
    })
    .set(authHeader);

  return body;
};

export const getScoreLogs = async (
  workerUserId: string,
  pageSize = SCORE_LOG_API_PAGE_SIZE,
  page = DEFAULT_PAGE_NUMBER,
  filter: {
    workplaceId?: string;
    createdAt?: { gte?: string; lte?: string };
    shiftId?: string;
  } = {}
): Promise<{ scoreLogs: ScoreLog[]; total: number }> => {
  const authHeader = await getAuthHeader();

  const filters =
    isDefined(filter.workplaceId) || isDefined(filter.createdAt) || isDefined(filter.shiftId)
      ? {
          filter: {
            ...(isDefined(filter.shiftId) && filter.shiftId !== ""
              ? { shiftIds: [filter.shiftId] }
              : {}),
            ...(isDefined(filter.workplaceId) ? { workplaceId: filter.workplaceId } : {}),
            ...(isDefined(filter.createdAt) ? { createdAt: filter.createdAt } : {}),
          },
        }
      : {};

  const url = `${attendanceScoreServiceUrl}/scores/${workerUserId}/history`;
  try {
    const { data } = await get({
      url,
      queryParams: { page, pageSize, ...filters },
      responseSchema: clipboardScoreHistoryResponse,
      customHeaders: {
        "Content-Type": "application/json",
        ...(isDefined(authHeader.Authorization) && { Authorization: authHeader.Authorization }),
      },
    });

    return { scoreLogs: data.scoreLogs ?? [], total: data.itemCount };
  } catch (error) {
    console.log({ error });
    return { scoreLogs: [], total: 0 };
  }
};

export const getAgentProfile = async (userId: string) => {
  const authHeader = await getAuthHeader();
  const { body } = await request.get(`${global.api}/user/get/${userId}`).set(authHeader);

  return body;
};

export async function acceptDispute(args: {
  scoreLogId: number;
  reason: string;
  category?: string;
}) {
  const { scoreLogId, reason, category } = args;
  const authHeader = await getAuthHeader();

  const response = await request
    .post(`${attendanceScoreServiceUrl}/scores/${scoreLogId}/reversals`)
    .set(authHeader)
    .send({ reason, category });

  return response;
}

export const adjustScore = async (adjustScoreReq: AdjustScoreArgs) => {
  const authHeader = await getAuthHeader();

  const response = await request
    .post(`${attendanceScoreServiceUrl}/scores/adjust`)
    .set(authHeader)
    .send(adjustScoreReq);

  return response;
};

export const getWorkersAttendanceRates = async (
  attendanceRateRequest: WorkersAttendanceRateRequest
): Promise<Record<string, number>> => {
  const authHeader = await getAuthHeader();

  const response = await request
    .get(`${attendanceScoreServiceUrl}/attendance-rate`)
    .set(authHeader)
    .query(attendanceRateRequest);
  return response?.body ?? {};
};

export const getPastRestrictions = async (
  workerUserId: string,
  inPastDays = 600
): Promise<GetRestrictionResponse[]> => {
  const authHeader = await getAuthHeader();

  const response = await request
    .get(`${attendanceScoreServiceUrl}/restrictions/${workerUserId}`)
    .set(authHeader)
    .query({ filter: { inPastDays } });

  return response.body.data.map(
    (record: { id: number; attributes: Omit<GetRestrictionResponse, "id"> }) => ({
      id: record.id,
      ...record.attributes,
    })
  );
};

export async function getWorkerPastDisputes(params: {
  workerUserId: string;
  pastDurationInMonths: number;
}): Promise<WorkerAttendanceScoreDispute[]> {
  const { workerUserId, pastDurationInMonths } = params;
  const authHeader = await getAuthHeader();

  const response = await request
    .get(`${attendanceScoreServiceUrl}/scores/reversals/${workerUserId}`)
    .set(authHeader)
    .query({
      "duration-months": pastDurationInMonths,
    });

  return response.body.reversals;
}
